/**
 * ComposeModal — cuadro de composición flotante para "Postear".
 *
 * Se abre encima de cualquier pantalla (lo dispara el botón "Postear" del
 * sidebar vía ?compose=1) sin navegar. Al publicar, cierra y lleva al feed
 * para ver el post. Mismo cuerpo que el composer inline: solo texto, con
 * @menciones y bloqueo de info de contacto.
 */
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { GuruAvatar } from '@/components/domain/GuruAvatar';
import { MentionTextarea } from '@/components/community/MentionTextarea';
import { useCreatePost, COMMUNITY_LIMITS } from '@/lib/api/community';
import { useUser } from '@/lib/auth';
import { useMyProfile } from '@/lib/api/account';
import { detectContactInfo, contactWarningMessage } from '@/lib/utils/contact-info-detector';
import { track } from '@/lib/analytics';
import { cn } from '@/lib/utils';

export function ComposeModal({
  onClose, initialBody, trendId,
}: {
  onClose: () => void;
  initialBody?: string;
  /** Tema del Radar que disparó el composer — el post queda vinculado (Fase 0 Radar Público). */
  trendId?: string;
}) {
  const createMut = useCreatePost();
  const user = useUser();
  const { data: profile } = useMyProfile();
  const navigate = useNavigate();
  // Si viene una pregunta del Radar, la precargamos como punto de partida.
  const [body, setBody] = useState(initialBody ? `${initialBody}\n\n` : '');

  // Métrica base del Radar Público: cuántos abren el composer desde un tema.
  useEffect(() => {
    if (trendId) track('radar_opine_started', { trend_id: trendId });
  }, [trendId]);

  const over = body.length > COMMUNITY_LIMITS.POST_MAX;
  const canPost = body.trim().length > 0 && !over;

  // Bloquear scroll del body + cerrar con Escape.
  useEffect(() => {
    const prev = document.body.style.overflow;
    const prevPad = document.body.style.paddingRight;
    // Al ocultar el scroll del body desaparece la barra (~15px) y el
    // contenido "salta" a la derecha. Compensamos ese ancho con padding
    // mientras el modal está abierto, así el fondo no se mueve.
    const scrollbarW = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = 'hidden';
    if (scrollbarW > 0) document.body.style.paddingRight = `${scrollbarW}px`;
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose(); }
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      document.body.style.paddingRight = prevPad;
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  async function publish() {
    if (!canPost) return;
    const detection = detectContactInfo(body);
    if (detection.hasMatches) {
      toast.error(contactWarningMessage(detection), { duration: 6000 });
      return;
    }
    try {
      await createMut.mutateAsync({ body_md: body, trend_id: trendId });
      toast.success('Post publicado');
      onClose();
      navigate('/community');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'No se pudo publicar');
    }
  }

  return createPortal(
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/50" onClick={onClose}>
      <div className="flex min-h-screen items-start justify-center p-4 pt-[10vh]">
        <div
          className="w-full max-w-xl rounded-2xl bg-white p-5 shadow-elevated"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between">
            <h2 className="font-display text-lg font-bold text-brand-text-strong">Nuevo post</h2>
            <button
              type="button"
              onClick={onClose}
              aria-label="Cerrar"
              className="inline-flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="mt-4 flex gap-3">
            <GuruAvatar
              avatarUrl={profile?.avatar_url}
              fullName={profile?.full_name ?? user?.email ?? null}
              size="md"
              className="ring-2 ring-white shadow-sm"
            />
            <div className="min-w-0 flex-1">
              <MentionTextarea
                value={body}
                onChange={setBody}
                autoFocus
                rows={5}
                maxLength={COMMUNITY_LIMITS.POST_MAX}
                placeholder="¿Qué estás pensando? Escribe @ para mencionar."
                className="w-full resize-none rounded-lg border border-transparent bg-transparent px-2 py-1.5 text-sm placeholder:text-muted-foreground focus-visible:outline-none"
              />
              <div className="mt-2 flex items-center justify-between border-t border-border/60 pt-2">
                <span className={cn('text-xs tabular-nums', over ? 'font-medium text-red-600' : 'text-muted-foreground')}>
                  {body.length} / {COMMUNITY_LIMITS.POST_MAX}
                </span>
                <Button variant="cta" size="sm" onClick={publish} disabled={!canPost} loading={createMut.isPending}>
                  Publicar
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
